import { useOktaAuth } from '@okta/okta-react';
import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

import { styled } from '@mui/material';

import type { RootState } from 'store';

import { AuthActions } from 'common/auth/actions';
import { clearPostLoginRedirect, getPostLoginRedirect } from 'common/auth/postLoginRedirect';
import { useDeviceHandoffActions } from 'common/deviceHandoff/hooks/useDeviceHandoffActions';
import {
    selectDeviceHandoffRejectionReason,
    selectIsDeviceHandoffModalOpen,
} from 'common/deviceHandoff/state/selectors';
import { AuthUserType } from 'common/graphql/types';
import type { ComponentWithoutHOCProps } from 'common/types/component';
import { Loading } from 'common/utils/loading';

import { withAuthLayout } from '../login/withAuthLayout';

import { OktaErrorDisplay } from './OktaErrorDisplay';

const _OktaPostLoginCallback = (): ReactElement => {
    const { t } = useTranslation('auth');
    const { t: tGlobal } = useTranslation('global');
    const { authState } = useOktaAuth();
    const auth = useSelector((state: RootState) => state.auth);
    const authError = useSelector((state: RootState) => state.auth.error);
    const isDeviceHandoffModalOpen = useSelector(selectIsDeviceHandoffModalOpen);
    const handoffRejectionReason = useSelector(selectDeviceHandoffRejectionReason);
    const dispatch = useDispatch();
    const { submitCredentials } = useDeviceHandoffActions();
    const postLoginRedirectUrl = getPostLoginRedirect();
    const isOktaLogin =
        auth.activeUser?.userType !== AuthUserType.PATIENT && auth.activeUser?.authenticationMethod === 'OKTA';
    const isDeviceHandoffLogin = isDeviceHandoffModalOpen;

    useEffect(() => {
        if (authState?.isAuthenticated) {
            if (isDeviceHandoffLogin) {
                submitCredentials(null, null, {
                    idToken: authState.idToken.idToken,
                    accessToken: authState.accessToken.accessToken,
                    refreshToken: authState.refreshToken as unknown as string,
                    expiry: authState.idToken.expiresAt,
                });
            } else {
                dispatch(
                    AuthActions.loginRequest(null, null, {
                        accessToken: authState.accessToken.accessToken,
                        idToken: authState.idToken.idToken,
                        refreshToken: authState.refreshToken,
                    })
                );
            }
        }
    }, [authState?.isAuthenticated]);

    const handleGoLoginPage = () => {
        window.location.href = '/';
    };

    if (authError) {
        return <OktaErrorDisplay error={authError} onGoToLogin={handleGoLoginPage} />;
    }

    if (authState?.isAuthenticated === false) {
        return <Navigate to="/" replace />;
    }

    if (handoffRejectionReason) {
        return (
            <OktaErrorDisplay
                error={{ message: tGlobal('switchuser.not_valid_second_user') }}
                onGoToLogin={handleGoLoginPage}
            />
        );
    }

    if (!auth.activeUser || (postLoginRedirectUrl && !isOktaLogin)) {
        return (
            <div className="success">
                {!!authState?.idToken?.claims?.name && (
                    <WelcomeMessage>{t('login.sso.welcome', { name: authState.idToken.claims.name })}</WelcomeMessage>
                )}
                <div style={{ margin: '1em 0 2em' }}>{t('login.sso.loading_props')}</div>
                <Loading isLoading={true} />
            </div>
        );
    }

    if (postLoginRedirectUrl) {
        clearPostLoginRedirect();

        return <Navigate to={postLoginRedirectUrl} replace />;
    }

    return <Navigate to="/" replace />;
};

const WelcomeMessage = styled('div')({
    fontSize: '1.4rem',
    marginBottom: '1em',
    lineHeight: '1.1em',
});

const OktaPostLoginCallback: ComponentWithoutHOCProps<null> = withAuthLayout(_OktaPostLoginCallback);

export default OktaPostLoginCallback;
